import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import GpsFixedOutlinedIcon from "@mui/icons-material/GpsFixedOutlined";
import {
  Box, Button, Chip, LinearProgress, Stack, Tooltip, Typography,
} from "@mui/material";
import { PALETTE, SectionHeader, SectionPaper } from "../components/shared";
import { isSkillTestPassed } from "../utils/skillTestPass";

type Props = {
  result: any;
  questions?: any[];
  answers?: Record<string, any>;
  testMeta?: any;
  onBack: () => void;
  backLabel?: string;
};

export default function SkillTestResultView({
  result,
  questions: passedQs = [],
  answers = {},
  testMeta,
  onBack,
  backLabel = "Back to Tests",
}: Props) {
  const questions: any[] = result.questions?.length ? result.questions : passedQs;

  const qResultMap: Record<string, { status: string; scoreEarned: number }> = {};
  (result.questionResults ?? []).forEach((r: any) => {
    qResultMap[r.questionId] = { status: r.status, scoreEarned: r.scoreEarned ?? 0 };
  });

  const totalMarks = result.totalMarks || result.test?.totalMarks || testMeta?.totalMarks || 0;
  const passedPct = totalMarks ? Math.round((result.score / totalMarks) * 100) : 0;
  const total = result.correctCount + result.wrongCount + result.skippedCount;
  const accuracyPct = total > 0 ? Math.round((result.correctCount / total) * 100) : 0;
  const passingMarks = result.test?.passingMarks ?? testMeta?.passingMarks ?? 0;
  const passed = isSkillTestPassed(result.score ?? 0, totalMarks, passingMarks);
  const durationMin = testMeta?.durationMinutes ?? result.test?.durationMinutes ?? 0;
  const timeTaken = result.timeTakenSeconds ?? 0;
  const attemptedOn = result.submittedAt
    ? new Date(result.submittedAt).toLocaleDateString("en-GB", {
        day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
      })
    : "";

  const typeMap: Record<string, { correct: number; total: number }> = {};
  questions.forEach((q: any) => {
    const t = (q.type === "mcq" || q.type === "output" || q.type === "debugging") ? "MCQs"
      : q.type === "coding" ? "Coding Problems"
      : q.type === "short_answer" ? "Short Answer" : "Other";
    if (!typeMap[t]) typeMap[t] = { correct: 0, total: 0 };
    typeMap[t].total++;
    if (qResultMap[q._id ?? q.id]?.status === "correct") typeMap[t].correct++;
  });

  const fmtTime = (s: number) => `${Math.floor(s / 60)}m ${s % 60}s`;
  const stStyle = (st: string) => st === "correct" ? { label: "Correct", bg: "#f0fdf4", color: "#16a34a" }
    : st === "wrong" ? { label: "Wrong", bg: "#fef2f2", color: "#dc2626" }
    : { label: "Unattempted", bg: "#f8fafc", color: "#64748b" };

  const accent = passed ? "#16a34a" : "#dc2626";
  const title = result.test?.title ?? testMeta?.title ?? "Skill Test";

  const stats = [
    { label: "Correct", value: result.correctCount, color: "#16a34a", icon: <CheckCircleOutlineIcon sx={{ fontSize: 18 }} /> },
    { label: "Wrong", value: result.wrongCount, color: "#dc2626", icon: <CancelOutlinedIcon sx={{ fontSize: 18 }} /> },
    { label: "Skipped", value: result.skippedCount, color: "#64748b", icon: <RemoveCircleOutlineIcon sx={{ fontSize: 18 }} /> },
    { label: "Accuracy", value: `${accuracyPct}%`, color: "#1d4ed8", icon: <GpsFixedOutlinedIcon sx={{ fontSize: 18 }} /> },
  ];

  const summary = [
    { label: "Score", primary: `${result.score}`, secondary: `/ ${totalMarks}` },
    { label: "Time used", primary: fmtTime(timeTaken), secondary: durationMin ? `of ${durationMin} min` : "" },
    { label: "Percentage", primary: `${passedPct}%`, secondary: "" },
    { label: "Percentile", primary: `${result.percentile ?? 0}`, secondary: "" },
  ];

  return (
    <Box sx={{ maxWidth: 920, mx: "auto", py: 1.5, px: { xs: 0.5, sm: 0 } }}>
      {/* Top back button */}
      <Button
        startIcon={<ArrowBackIcon fontSize="small" />}
        onClick={onBack}
        size="small"
        sx={{
          textTransform: "none",
          color: PALETTE.textSecondary,
          fontSize: 13,
          mb: 1.5,
          px: 0.5,
          "&:hover": { bgcolor: "transparent", color: PALETTE.primary },
        }}
      >
        {backLabel}
      </Button>

      {/* Result header */}
      <SectionPaper sx={{ mb: 2 }}>
        <Box sx={{
          px: { xs: 2, sm: 2.5 },
          py: 2,
          borderLeft: `4px solid ${accent}`,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 2,
        }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
              <Chip
                size="small"
                label={passed ? "Passed" : "Not passed"}
                sx={{
                  height: 22,
                  fontSize: 11,
                  fontWeight: 700,
                  bgcolor: passed ? "#f0fdf4" : "#fef2f2",
                  color: accent,
                  border: `1px solid ${passed ? "#bbf7d0" : "#fecaca"}`,
                }}
              />
              {attemptedOn && (
                <Typography sx={{ fontSize: 12, color: PALETTE.textMuted }}>
                  {attemptedOn}
                </Typography>
              )}
            </Stack>
            <Typography sx={{
              fontSize: 18, fontWeight: 700, color: "#111827", lineHeight: 1.3,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {title}
            </Typography>
            <Typography sx={{ fontSize: 13, color: PALETTE.textSecondary, mt: 0.35 }}>
              {passed
                ? "You cleared the passing criteria for this test."
                : "You did not meet the passing criteria this time."}
            </Typography>
          </Box>

          <Box sx={{
            minWidth: { sm: 140 },
            textAlign: { xs: "left", sm: "right" },
            flexShrink: 0,
          }}>
            <Typography sx={{ fontSize: 28, fontWeight: 800, color: accent, lineHeight: 1.1 }}>
              {passedPct}%
            </Typography>
            <Typography sx={{ fontSize: 13, color: PALETTE.textSecondary, mt: 0.25 }}>
              {result.score} / {totalMarks} marks
            </Typography>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, passedPct)}
              sx={{
                mt: 1, height: 6, borderRadius: 3, bgcolor: "#f1f5f9",
                "& .MuiLinearProgress-bar": { bgcolor: accent, borderRadius: 3 },
              }}
            />
          </Box>
        </Box>
      </SectionPaper>

      {/* Stat cards */}
      <Box sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
        gap: 1.25,
        mb: 2,
      }}>
        {stats.map(({ label, value, color, icon }) => (
          <SectionPaper key={label} sx={{ p: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Box sx={{
                color, display: "flex", p: 0.75, borderRadius: "8px",
                bgcolor: `${color}14`,
              }}>
                {icon}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 11, color: PALETTE.textSecondary, fontWeight: 500 }}>
                  {label}
                </Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 700, color, lineHeight: 1.2 }}>
                  {value}
                </Typography>
              </Box>
            </Stack>
          </SectionPaper>
        ))}
      </Box>

      {/* Summary row */}
      <SectionPaper sx={{ mb: 2 }}>
        <Box sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
        }}>
          {summary.map(({ label, primary, secondary }, i) => (
            <Box
              key={label}
              sx={{
                px: 2, py: 1.75,
                borderRight: {
                  xs: i % 2 === 0 ? `1px solid ${PALETTE.border}` : "none",
                  sm: i < summary.length - 1 ? `1px solid ${PALETTE.border}` : "none",
                },
                borderBottom: {
                  xs: i < 2 ? `1px solid ${PALETTE.border}` : "none",
                  sm: "none",
                },
              }}
            >
              <Typography sx={{
                fontSize: 11, fontWeight: 600, color: PALETTE.textMuted,
                letterSpacing: "0.04em", textTransform: "uppercase", mb: 0.5,
              }}>
                {label}
              </Typography>
              <Stack direction="row" alignItems="baseline" spacing={0.5}>
                <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#111827" }}>
                  {primary}
                </Typography>
                {secondary && (
                  <Typography sx={{ fontSize: 12, color: PALETTE.textSecondary }}>
                    {secondary}
                  </Typography>
                )}
              </Stack>
            </Box>
          ))}
        </Box>
      </SectionPaper>

      {/* Problem type breakdown */}
      {Object.keys(typeMap).length > 0 && (
        <SectionPaper sx={{ mb: 2 }}>
          <SectionHeader title="Problem type" />
          <Box sx={{ overflowX: "auto" }}>
            <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: 360 }}>
              <Box component="thead">
                <Box component="tr">
                  {["Type", "Correct", "Total"].map((h) => (
                    <Box
                      component="th"
                      key={h}
                      sx={{
                        px: 2, py: 1.1, textAlign: "left", fontSize: 12, fontWeight: 700,
                        color: "#374151", borderBottom: `1px solid ${PALETTE.border}`,
                        bgcolor: PALETTE.bgLight,
                      }}
                    >
                      {h}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {Object.entries(typeMap).map(([type, { correct, total: tot }], i, arr) => (
                  <Box
                    component="tr"
                    key={type}
                    sx={{
                      borderBottom: i < arr.length - 1 ? `1px solid ${PALETTE.border}` : "none",
                      "&:hover": { bgcolor: PALETTE.bgHover },
                    }}
                  >
                    <Box component="td" sx={{ px: 2, py: 1.25, fontSize: 13, color: "#374151" }}>{type}</Box>
                    <Box component="td" sx={{ px: 2, py: 1.25, fontSize: 13, fontWeight: 700, color: "#16a34a" }}>{correct}</Box>
                    <Box component="td" sx={{ px: 2, py: 1.25, fontSize: 13, color: "#374151" }}>{tot}</Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </SectionPaper>
      )}

      {/* Question breakdown */}
      <SectionPaper>
        <SectionHeader title="Problem breakdown" />
        <Box sx={{ overflowX: "auto" }}>
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
            <Box component="thead">
              <Box component="tr">
                {["Problem", "Type", "Difficulty", "Status", "Score"].map((h) => (
                  <Box
                    component="th"
                    key={h}
                    sx={{
                      px: 2, py: 1.1, textAlign: "left", fontSize: 12, fontWeight: 700,
                      color: "#374151", borderBottom: `1px solid ${PALETTE.border}`,
                      bgcolor: PALETTE.bgLight, whiteSpace: "nowrap",
                    }}
                  >
                    {h}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {questions.map((q: any, i: number) => {
                const qid = q._id ?? q.id;
                const qr = qResultMap[qid];
                const status = qr?.status ?? "skipped";
                const st = stStyle(status);
                const qMarks = q.marks ?? 1;
                const scoreEarned = qr?.scoreEarned ?? 0;
                const qType = q.type === "mcq" ? "MCQ"
                  : q.type === "coding" ? "Coding"
                  : q.type === "short_answer" ? "Short Answer"
                  : q.type === "output" ? "Output"
                  : q.type === "debugging" ? "Debugging"
                  : q.type;
                return (
                  <Box
                    component="tr"
                    key={qid ?? i}
                    sx={{
                      borderBottom: i < questions.length - 1 ? `1px solid ${PALETTE.border}` : "none",
                      "&:hover": { bgcolor: PALETTE.bgHover },
                    }}
                  >
                    <Box component="td" sx={{ px: 2, py: 1.25, fontSize: 13, maxWidth: 260, color: "#374151" }}>
                      <Tooltip title={q.questionText || ""}>
                        <span>
                          {q.questionText?.length > 50
                            ? `${q.questionText.slice(0, 50)}…`
                            : q.questionText}
                        </span>
                      </Tooltip>
                    </Box>
                    <Box component="td" sx={{ px: 2, py: 1.25, fontSize: 13, whiteSpace: "nowrap", color: "#374151" }}>
                      {qType}
                    </Box>
                    <Box component="td" sx={{
                      px: 2, py: 1.25, fontSize: 13, textTransform: "capitalize",
                      whiteSpace: "nowrap", color: "#374151",
                    }}>
                      {q.difficulty ?? "—"}
                    </Box>
                    <Box component="td" sx={{ px: 2, py: 1.25 }}>
                      <Box sx={{
                        display: "inline-block", px: 1, py: 0.2, borderRadius: "6px",
                        fontSize: 11, fontWeight: 600, bgcolor: st.bg, color: st.color,
                      }}>
                        {st.label}
                      </Box>
                    </Box>
                    <Box component="td" sx={{
                      px: 2, py: 1.25, fontSize: 13, fontWeight: 600,
                      whiteSpace: "nowrap", color: "#374151",
                    }}>
                      {scoreEarned} / {qMarks}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      </SectionPaper>
    </Box>
  );
}
